import React, { useState } from 'react';
import {
  Card,
  Typography,
  Flex,
  Tag,
  Button,
  Table,
  Avatar,
} from 'antd';
import {
  DownOutlined,
  UpOutlined,
  CarOutlined,
  UserOutlined,
  RiseOutlined,
  TeamOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import VinCopy from './VinCopy';
import LotCopy from './LotCopy';
import { useIsMobile } from '../hooks/useResponsive';

const LotCard = ({ lot }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { lang } = useParams();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleUserClick = (userId, userRole) => {
    if (!userId) return;

    if (userRole === 'dealer') {
      navigate(`/${lang}/dealers/${userId}`);
    } else if (userRole === 'subdealer') {
      navigate(`/${lang}/subdealers/${userId}`);
    } else {
      navigate(`/${lang}/subdealers/${userId}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEventColor = (event) => {
    const eventLower = event?.toLowerCase() || '';
    if (eventLower.includes('copart')) return 'blue';
    if (eventLower.includes('iaai')) return 'purple';
    return 'default';
  };

  const getAuctionUrl = (lotNumber, host, event) => {
    if (!lotNumber) return null;

    const lotNum = lotNumber.toString().trim();
    const hostLower = (host || '').toLowerCase();
    const eventLower = (event || '').toLowerCase();
    const combined = `${hostLower} ${eventLower}`;

    if (combined.includes('copart')) {
      return `https://www.copart.com/lot/${lotNum}/`;
    }
    if (combined.includes('iaai') && (combined.includes('canada') || combined.includes('can'))) {
      return `https://www.iaai.com/VehicleDetail/${lotNum}~CA`;
    }
    if (combined.includes('iaai')) {
      return `https://www.iaai.com/VehicleDetail/${lotNum}~US`;
    }
    if (combined.includes('manheim')) {
      return `https://www.manheim.com/vehicles/${lotNum}`;
    }
    if (combined.includes('acv')) {
      return `https://app.acvauctions.com/marketplace/${lotNum}`;
    }

    return null;
  };

  const handleLotClick = (e) => {
    e.stopPropagation();
    const url = getAuctionUrl(lot.lotNumber, lot.host, lot.event);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isMobile = useIsMobile();

  const DesktopRow = () => (
    <Flex align="center" gap={8} wrap="wrap">
      <Flex align="center" gap={4} style={{ width: 180, flexShrink: 0 }}>
        <CarOutlined style={{ color: 'var(--colorPrimary)' }} />
        <Flex vertical style={{ minWidth: 0, flex: 1 }}>
          <Flex align="center" gap={4}>
            <Typography.Link
              onClick={handleLotClick}
              style={{
                cursor: getAuctionUrl(lot.lotNumber, lot.host, lot.event) ? 'pointer' : 'default',
              }}
            >
              Lot {lot.lotNumber}
            </Typography.Link>
            {lot.lotNumber && <LotCopy lotNumber={lot.lotNumber} size="small" />}
          </Flex>
          <Flex align="center">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {lot.vin || 'N/A'}
            </Typography.Text>
            {lot.vin && <VinCopy vin={lot.vin} size="small" />}
          </Flex>
        </Flex>
      </Flex>

      <div style={{ width: 90, flexShrink: 0 }}>
        {lot.event && (
          <Tag color={getEventColor(lot.event)} style={{ height: 22, fontSize: '0.7rem' }}>
            {lot.event.toUpperCase()}
          </Tag>
        )}
      </div>

      <Typography.Text type="secondary" style={{ width: 120, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {lot.host || 'N/A'}
      </Typography.Text>

      <Flex align="center" gap={4} style={{ width: 110, flexShrink: 0 }}>
        <TrophyOutlined style={{ color: '#22c55e', fontSize: 16 }} />
        <Flex vertical>
          <Typography.Text type="secondary" style={{ fontSize: 10, lineHeight: 1 }}>
            Highest
          </Typography.Text>
          <Typography.Text strong style={{ color: '#22c55e' }}>
            ${lot.highestBid?.toLocaleString() || '0'}
          </Typography.Text>
        </Flex>
      </Flex>

      <Flex align="center" gap={4} style={{ width: 200, flexShrink: 0, minWidth: 0 }}>
        <UserOutlined style={{ fontSize: 16, color: 'var(--colorTextSecondary)' }} />
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleUserClick(lot.highestBidUser, lot.bids?.[0]?.userRole);
          }}
          style={{ padding: 0, height: 'auto' }}
        >
          {lot.highestBidUserName || 'Unknown'}
        </Button>
      </Flex>

      <Flex align="center" gap={4} style={{ width: 80, flexShrink: 0 }}>
        <RiseOutlined style={{ fontSize: 16, color: 'var(--colorPrimary)' }} />
        <Typography.Text strong>{lot.totalBids || 0}</Typography.Text>
      </Flex>

      <Flex align="center" gap={4} style={{ width: 80, flexShrink: 0 }}>
        <TeamOutlined style={{ fontSize: 16, color: 'var(--colorTextSecondary)' }} />
        <Typography.Text strong>{lot.uniqueUsersCount || 0}</Typography.Text>
      </Flex>

      <Flex align="center" gap={4} style={{ flex: 1, minWidth: 0 }}>
        <ClockCircleOutlined style={{ fontSize: 16, color: 'var(--colorTextSecondary)' }} />
        <Typography.Text type="secondary" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {lot.firstBidTime && lot.lastBidTime
            ? `${formatDate(lot.firstBidTime)} → ${formatDate(lot.lastBidTime)}`
            : 'N/A'}
        </Typography.Text>
      </Flex>

      <Button
        type="text"
        size="small"
        icon={expanded ? <UpOutlined /> : <DownOutlined />}
        onClick={handleExpandClick}
      />
    </Flex>
  );

  const MobileStack = () => (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="flex-start">
        <Flex align="center" gap={8}>
          <CarOutlined style={{ color: 'var(--colorPrimary)' }} />
          <Flex vertical>
            <Flex align="center" gap={4}>
              <Typography.Link onClick={handleLotClick}>Lot {lot.lotNumber}</Typography.Link>
              {lot.lotNumber && <LotCopy lotNumber={lot.lotNumber} size="small" />}
            </Flex>
            <Flex align="center">
              <Typography.Text type="secondary">{lot.vin || 'N/A'}</Typography.Text>
              {lot.vin && <VinCopy vin={lot.vin} size="small" />}
            </Flex>
          </Flex>
        </Flex>
        <Flex vertical align="flex-end">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Highest Bid
          </Typography.Text>
          <Typography.Text strong style={{ color: '#22c55e' }}>
            ${lot.highestBid?.toLocaleString() || '0'}
          </Typography.Text>
        </Flex>
      </Flex>

      <Flex wrap="wrap" gap={8} align="center" justify="space-between">
        <Flex gap={8} align="center">
          {lot.event && (
            <Tag color={getEventColor(lot.event)} style={{ height: 24, fontSize: '0.75rem' }}>
              {lot.event.toUpperCase()}
            </Tag>
          )}
          <Typography.Text type="secondary">{lot.host || 'N/A'}</Typography.Text>
        </Flex>
        <Flex align="center" gap={4}>
          <UserOutlined style={{ fontSize: 16 }} />
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleUserClick(lot.highestBidUser, lot.bids?.[0]?.userRole);
            }}
            style={{ padding: 0, height: 'auto' }}
          >
            {lot.highestBidUserName || 'Unknown'}
          </Button>
        </Flex>
      </Flex>

      <Flex justify="space-between" align="center" style={{ background: 'var(--colorBgLayout)', padding: 8, borderRadius: 8 }}>
        <Flex gap={16}>
          <Flex align="center" gap={4}>
            <RiseOutlined style={{ fontSize: 16, color: 'var(--colorPrimary)' }} />
            <Typography.Text strong style={{ fontSize: 12 }}>
              {lot.totalBids || 0} Bids
            </Typography.Text>
          </Flex>
          <Flex align="center" gap={4}>
            <TeamOutlined style={{ fontSize: 16, color: 'var(--colorTextSecondary)' }} />
            <Typography.Text strong style={{ fontSize: 12 }}>
              {lot.uniqueUsersCount || 0} Users
            </Typography.Text>
          </Flex>
        </Flex>
        <Button
          type="text"
          size="small"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          onClick={handleExpandClick}
        />
      </Flex>

      <Flex align="center" gap={4}>
        <ClockCircleOutlined style={{ fontSize: 14, color: 'var(--colorTextSecondary)' }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {lot.firstBidTime && lot.lastBidTime
            ? `${formatDate(lot.firstBidTime)} → ${formatDate(lot.lastBidTime)}`
            : 'N/A'}
        </Typography.Text>
      </Flex>
    </Flex>
  );

  const bidColumns = [
    {
      title: '#',
      key: 'index',
      width: 40,
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text style={{ fontSize: 12 }}>{record.index}</Typography.Text>
          {record.isHighestBid && <TrophyOutlined style={{ color: '#22c55e', fontSize: 14 }} />}
        </Flex>
      ),
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const bid = lot.bids?.[record.index - 1];
        return (
          <Flex align="center" gap={4}>
            <Avatar size={20} style={{ fontSize: 10 }}>
              {(record.appUserName || 'U')[0]}
            </Avatar>
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleUserClick(bid?.appUserCompanyId, bid?.userRole);
              }}
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
            >
              {record.appUserName || 'Unknown'}
            </Button>
          </Flex>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'bidAmount',
      key: 'amount',
      render: (val, record) => (
        <Typography.Text strong={record.isHighestBid} style={{ color: record.isHighestBid ? '#22c55e' : 'var(--colorPrimary)' }}>
          {val}
        </Typography.Text>
      ),
    },
    { title: 'Time', dataIndex: 'time', key: 'time' },
    ...(lot.bids?.[0]?.branchName ? [{ title: 'Branch', dataIndex: 'branchName', key: 'branch' }] : []),
    ...(lot.bids?.[0]?.dealerName ? [{ title: 'Dealer', dataIndex: 'dealerName', key: 'dealer' }] : []),
    ...(lot.bids?.[0]?.userRole
      ? [
          {
            title: 'Role',
            dataIndex: 'userRole',
            key: 'role',
            render: (val) => <Tag style={{ height: 20, fontSize: 10 }}>{val || 'N/A'}</Tag>,
          },
        ]
      : []),
  ];

  const bidDataSource = (lot.bids || []).map((bid, index) => ({
    key: bid._id || index,
    index: index + 1,
    appUserName: bid.appUserName || 'Unknown',
    bidAmount: `$${bid.bidAmount?.toLocaleString() || bid.bid}`,
    time: formatDate(bid.createdAt || bid.timeStamp),
    branchName: bid.branchName || 'N/A',
    dealerName: bid.dealerName || 'N/A',
    userRole: bid.userRole || 'N/A',
    isHighestBid: bid.bidAmount === lot.highestBid,
  }));

  return (
    <Card
      size="small"
      style={{
        marginBottom: 8,
        transition: 'all 0.2s ease',
      }}
      styles={{ body: { padding: 12 } }}
    >
      {isMobile ? <MobileStack /> : <DesktopRow />}

      {expanded && (
        <div style={{ marginTop: 8, borderTop: '1px dashed var(--colorBorder)', paddingTop: 8 }}>
          {lot.bids && lot.bids.length > 0 ? (
            <Table
              size="small"
              columns={bidColumns}
              dataSource={bidDataSource}
              pagination={false}
              rowClassName={(record) => (record.isHighestBid ? 'ant-table-row-bid-highlight' : '')}
            />
          ) : (
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 8 }}>
              No bid history available
            </Typography.Text>
          )}
        </div>
      )}
    </Card>
  );
};

export default LotCard;
